/**
 * The run control panel: one collapsible strip at the top of the card that
 * reports every run, practice or graded, in the same shape.
 *
 * It exists because the two things it replaces disagreed with each other. A
 * graded run's status strip said "running" while the terminal area underneath
 * said "starting sandbox", because each of them interpreted the payload for
 * itself. The fix is structural rather than editorial: `derivePhase` runs once
 * per poll and everything downstream -- strip, lane grid, locked terminal card,
 * screen-reader announcements -- renders from that one value. They cannot drift
 * again without deleting the phase machine.
 *
 * The other rule that shapes this file: the client never owns time or truth.
 * Elapsed is wall-clock arithmetic against the run's absolute start time, with
 * the browser's clock error measured against the server's and subtracted. Both
 * of the obvious alternatives are wrong in a way users hit immediately: a timer
 * started on mount resets to zero on every refresh, and a counter extrapolated
 * from the last poll freezes whenever the device sleeps.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './RunControlPanel.css';

export type Phase =
  | 'idle' | 'queued' | 'starting' | 'running' | 'finishing'
  | 'succeeded' | 'failed' | 'canceled' | 'error';

export interface LaneUsage {
  requests: number;
  /** NEW tokens: content sent to the model for the first time, deduplicated
   *  across turns. The competition's tie-breaker, fewest wins. */
  input_tokens: number;
  output_tokens: number;
  /** Every prompt in full, re-counted per turn. Honest cost, never ranked on. */
  billed_input_tokens: number;
  /** Fragments the tokenizer refused; nonzero means input_tokens undercounts. */
  tokenize_errors: number;
}

export interface Lane {
  task: string;
  run: number;
  state: 'queued' | 'running' | 'done';
  elapsed: number | null;
  exit_code: number | null;
  timed_out: boolean;
  usage: LaneUsage | null;
}

export interface Progress {
  started_at: string | null;
  finished_at: string | null;
  elapsed: number | null;
  lanes: Lane[];
  total: LaneUsage | null;
  n_runs: number | null;
  sampled_at: number | null;
}

const PHASE_GLYPH: Record<Phase, string> = {
  idle: '·', queued: '◌', starting: '', running: '', finishing: '',
  succeeded: '✓', failed: '✕', canceled: '⊘', error: '✕',
};

const PHASE_WORD: Record<Phase, string> = {
  idle: 'No run yet', queued: 'Queued', starting: 'Starting sandbox',
  running: 'Running', finishing: 'Finishing', succeeded: 'Succeeded',
  failed: 'Failed', canceled: 'Canceled', error: 'Failed',
};

const LIVE_PHASES: Phase[] = ['queued', 'starting', 'running', 'finishing'];
export const isLive = (p: Phase) => LIVE_PHASES.includes(p);

/** The single interpretation of the payload. Nothing else may read `state`. */
export function derivePhase(state: string | null | undefined, lanes: Lane[]): Phase {
  if (!state) return 'idle';
  if (state === 'queued') return 'queued';
  if (state === 'running') {
    if (!lanes.length || lanes.every((l) => l.state === 'queued')) return 'starting';
    if (lanes.every((l) => l.state === 'done')) return 'finishing';
    return 'running';
  }
  if (state === 'succeeded' || state === 'failed' || state === 'canceled' || state === 'error') {
    return state;
  }
  return 'running';
}

// ---------------------------------------------------------------- formatting

/** Exact with separators under 100k, then 3 significant figures. Token counts
 *  get compared between submissions, so precision matters more than brevity
 *  right up to the point where the digits stop being readable. */
const fmtTokens = (n: number | null | undefined, exact = false): string => {
  if (n == null || !Number.isFinite(n)) return '-';
  if (exact && n < 10_000_000) return n.toLocaleString();
  if (n < 100_000) return n.toLocaleString();
  if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
};

/** m:ss, or h:mm:ss once it earns the third field. */
const fmtClock = (seconds: number | null | undefined): string => {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '-';
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
};

const taskShort = (taskId: string) => taskId.replace(/^\d+_/, '').replace(/_/g, ' ');

// --------------------------------------------------------------------- hooks

/**
 * Seconds elapsed for the run.
 *
 * For a LIVE run this is wall-clock arithmetic against the run's absolute
 * start time, not an extrapolated counter: `(now - started_at) - skew`. That
 * distinction is the whole point. A counter that ticks forward from the last
 * polled value is only as good as the polling, so it froze whenever the device
 * did -- a phone that slept for half an hour came back still showing three
 * minutes. Absolute arithmetic cannot drift, cannot freeze, and does not care
 * how long the page was suspended or whether it was restored from bfcache.
 *
 * `skew` reconciles the browser's clock with the server's. It is measured, not
 * assumed: the server sends both `started_at` and its own `elapsed`, so the
 * difference between them is exactly this machine's error, and subtracting it
 * makes a wrong device clock harmless.
 *
 * Measured ONCE PER PAYLOAD, in an effect, and that is load-bearing. Measuring
 * it during render re-derived it on every one-second tick from an unchanged
 * `elapsed` and an advanced `Date.now()`, so the correction grew by exactly the
 * second that had just passed and the result collapsed back to whatever the
 * last poll said. The clock did not freeze, but it did not tick either: it
 * jumped once per poll, up to fifteen seconds at rest, which is the same
 * symptom this hook was written to remove.
 *
 * A FINISHED run reports the server's frozen number and stops ticking.
 */
function useElapsed(progress: Progress | null, ticking: boolean): number | null {
  const [, force] = useState(0);
  // State, not a ref, so a corrected skew is on screen immediately. Held in a
  // ref it was applied but not rendered, so a payload that moved the clock (a
  // resumed tab, a wrong device clock) only showed up on the next tick.
  // useLayoutEffect so the correction lands before paint rather than one frame
  // of the wrong number later.
  const [skew, setSkew] = useState(0);

  const startedAt = progress?.started_at ?? null;
  const serverElapsed = progress?.elapsed ?? null;
  useLayoutEffect(() => {
    const started = startedAt ? Date.parse(startedAt) : NaN;
    if (!Number.isFinite(started) || serverElapsed == null) return;
    const next = (Date.now() - started) / 1000 - serverElapsed;
    // Ignore sub-second churn: every poll measures a slightly different network
    // latency, and re-rendering for a tenth of a second buys nothing.
    setSkew((prev) => (Math.abs(next - prev) > 0.5 ? next : prev));
  }, [startedAt, serverElapsed]);

  useEffect(() => {
    if (!ticking) return undefined;
    const id = setInterval(() => force((n) => n + 1), 1000);
    // A suspended tab does not run timers, so recompute the moment it comes
    // back rather than waiting up to a second. `pageshow` matters as much as
    // `visibilitychange`: a bfcache restore on mobile Safari fires only that.
    const wake = () => force((n) => n + 1);
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('pageshow', wake);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('pageshow', wake);
    };
  }, [ticking]);

  const startedMs = startedAt ? Date.parse(startedAt) : NaN;
  if (!ticking) return serverElapsed;
  if (!Number.isFinite(startedMs)) return serverElapsed;
  return Math.max(0, (Date.now() - startedMs) / 1000 - skew);
}

/**
 * Output tokens per second over a 20-second sliding window, smoothed.
 *
 * Sampled on the SERVER's clock (`sampled_at`), so a throttled background tab
 * or a skewed laptop clock cannot invent throughput. The window is long enough
 * to flatten per-poll jitter and bursty generation, short enough to notice a
 * stall within about ten seconds; the EWMA settles over roughly three polls.
 * The displayed value is quantised so it stops dancing between renders.
 */
function useThroughput(sampledAt: number | null, outputTokens: number | null,
                       startedAt: string | null): number | null {
  const buf = useRef<{ t: number; out: number }[]>([]);
  const ewma = useRef<number | null>(null);
  const run = useRef<string | null>(null);

  if (run.current !== startedAt) {           // a new run starts a new series
    run.current = startedAt ?? null;
    buf.current = [];
    ewma.current = null;
  }
  if (sampledAt != null && outputTokens != null) {
    const last = buf.current[buf.current.length - 1];
    if (!last || sampledAt > last.t) {       // ignore replayed samples
      buf.current.push({ t: sampledAt, out: outputTokens });
      const cutoff = sampledAt - 30;
      buf.current = buf.current.filter((s) => s.t >= cutoff);
    }
  }

  const samples = buf.current;
  const newest = samples[samples.length - 1];
  if (!newest) return null;
  const window = samples.filter((s) => s.t >= newest.t - 20);
  if (window.length < 2 || newest.t - window[0].t < 5) return null;  // warm-up
  const raw = Math.max(0, (newest.out - window[0].out) / (newest.t - window[0].t));
  ewma.current = ewma.current == null ? raw : 0.35 * raw + 0.65 * ewma.current;
  return ewma.current;
}

const quantiseRate = (r: number | null): string => {
  if (r == null) return '-';
  const q = r < 10 ? Math.round(r * 10) / 10 : r < 100 ? Math.round(r) : Math.round(r / 5) * 5;
  return `~${q} tok/s`;
};

/**
 * Counts up to the latest token sample over one live poll (1.5s), so a counter
 * that is sampled in steps reads as a steady climb. Drops and reduced motion
 * show the new value at once.
 */
const COUNT_MS = 1500;
function useCountUp(target: number | null | undefined): number | null | undefined {
  const [shown, setShown] = useState(target);
  const shownRef = useRef(target);
  useEffect(() => {
    const from = shownRef.current;
    const still = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (target == null || from == null || target <= from || still
        || typeof window.requestAnimationFrame !== 'function') {
      shownRef.current = target;
      setShown(target);
      return;
    }
    const start = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / COUNT_MS);
      shownRef.current = Math.round(from + (target - from) * t);
      setShown(shownRef.current);
      if (t < 1) frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(frame);
  }, [target]);
  return shown;
}

// ----------------------------------------------------------------- lane cell

const LANE_GLYPH = { queued: '◌', running: '▶', done: '✓' } as const;

const LaneCell: React.FC<{
  lane: Lane;
  selected: boolean;
  onSelect: () => void;
}> = ({ lane, selected, onSelect }) => {
  const failed = lane.state === 'done' && (lane.timed_out || (lane.exit_code ?? 0) !== 0);
  // Timed out wins over the exit code: a timeout that exits 124 reads as a
  // timeout, not as "exit 124".
  const badge = lane.timed_out ? 'timed out'
    : lane.state === 'done' && (lane.exit_code ?? 0) !== 0 ? `exit ${lane.exit_code}`
    : null;
  const glyph = lane.timed_out ? '⏱' : failed ? '✕' : LANE_GLYPH[lane.state];
  const tokens = useCountUp(lane.usage?.input_tokens ?? null) ?? null;
  // No per-lane clock. Twelve near-identical timers ticking in lockstep is
  // noise: the lanes start together, so they all read the same thing, and the
  // number anyone actually wants is the run's own elapsed time in the strip.
  const approx = (lane.usage?.tokenize_errors ?? 0) > 0;

  return (
    <button
      type="button"
      role="gridcell"
      aria-selected={selected}
      className={`rcp-lane rcp-lane--${lane.state}${failed ? ' rcp-lane--bad' : ''}${
        selected ? ' rcp-lane--selected' : ''}`}
      onClick={onSelect}
      aria-label={
        `${taskShort(lane.task)}, run ${lane.run}, ${lane.state}` +
        (lane.elapsed != null ? `, ${fmtClock(lane.elapsed)}` : '') +
        (tokens != null ? `, ${tokens.toLocaleString()} new tokens` : '') +
        (badge ? `, ${badge}` : '')
      }
    >
      <span className="rcp-lane__glyph" aria-hidden="true">{glyph}</span>
      <span className="rcp-lane__run">run {lane.run}</span>
      <span className="rcp-lane__tok">
        {lane.state === 'queued' ? '-' : `${approx ? '≈' : ''}${fmtTokens(tokens)}`}
      </span>
      <span className="rcp-lane__badge">{badge}</span>
    </button>
  );
};

// --------------------------------------------------------------------- panel

export interface RunControlPanelProps {
  phase: Phase;
  progress: Progress | null;
  /** 'graded' hides the terminals; the panel itself is identical either way. */
  mode: 'practice' | 'graded';
  nRuns: number;
  queuePosition: number | null;
  queueLength: number;
  selected: { task: string; run: number } | null;
  onSelectLane: (task: string, run: number) => void;
  tasks: { id: string; label: string }[];
  /** The Cancel control, owned by the parent, rendered at the strip's right edge. */
  action?: React.ReactNode;
}

const RunControlPanel: React.FC<RunControlPanelProps> = ({
  phase, progress, mode, nRuns, queuePosition, queueLength,
  selected, onSelectLane, tasks, action,
}) => {
  const [open, setOpen] = useState(true);
  const live = isLive(phase);

  const lanes = progress?.lanes ?? [];
  const elapsed = useElapsed(progress, live);
  const rate = useThroughput(progress?.sampled_at ?? null,
                             progress?.total?.output_tokens ?? null,
                             progress?.started_at ?? null);

  const done = lanes.filter((l) => l.state === 'done').length;
  // tasks[0] is not guaranteed: a loading parent or a competition without
  // the four-task protocol would otherwise take the whole panel down.
  const firstTask = tasks[0]?.id ?? '';
  // Max, not a fallback: while the sandbox registers lanes one at a time,
  // lanes.length climbs from 0 to 12 and the strip would read "3/3 lanes",
  // which looks finished, next to a meter still showing twelve cells.
  const expected = Math.max(
    lanes.length, tasks.length * Math.max(1, progress?.n_runs || nRuns || 1));
  const total = progress?.total ?? null;
  const undercount = (total?.tokenize_errors ?? 0) > 0;
  const totalIn = useCountUp(total?.input_tokens);
  const totalOut = useCountUp(total?.output_tokens);
  const speed = live ? (rate == null ? null : quantiseRate(rate))
    : total?.output_tokens != null && elapsed ? `avg ${Math.round(total.output_tokens / elapsed)} tok/s` : null;

  // Placeholder lanes before the sandbox has registered any, so the grid has
  // its final shape from the first render instead of growing into it.
  const grid = useMemo(() => {
    const byKey = new Map(lanes.map((l) => [`${l.task}/${l.run}`, l]));
    const runs = Array.from({ length: Math.max(1, progress?.n_runs || nRuns || 1) },
                            (_, i) => i + 1);
    return tasks.map((task) => ({
      task,
      rows: runs.map((run) => byKey.get(`${task.id}/${run}`) ?? {
        task: task.id, run, state: 'queued' as const, elapsed: null,
        exit_code: null, timed_out: false, usage: null,
      }),
    }));
  }, [lanes, tasks, nRuns, progress?.n_runs]);

  // Announce transitions, not ticks. Coalesced so a screen reader gets one
  // sentence every few seconds rather than a number every poll.
  const [announcement, setAnnouncement] = useState('');
  const lastDone = useRef(-1);
  const lastPhase = useRef<Phase | null>(null);
  useEffect(() => {
    const phaseChanged = lastPhase.current !== phase;
    const laneChanged = lastDone.current !== done;
    lastPhase.current = phase;
    lastDone.current = done;
    if (!phaseChanged && !laneChanged) return;
    if (!live) {
      setAnnouncement(
        `Run ${PHASE_WORD[phase].toLowerCase()}. ${done} of ${expected} lanes done` +
        (elapsed != null ? ` in ${fmtClock(elapsed)}` : '') + '.');
      return;
    }
    setAnnouncement(`${PHASE_WORD[phase]}. ${done} of ${expected} lanes done.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, done, expected, live]);

  const strip = (
    <>
      <span className="rcp-strip__phase">
        {/* A CSS ring, not a braille frame cycled from React state. The old
            one re-rendered the whole panel ten times a second and stepped
            through glyphs of different widths and ink, so it visibly stuttered
            and nudged its neighbours. This animates on the compositor and
            costs no renders at all. */}
        {live && phase !== 'queued' ? (
          <span className="rcp-spinner" aria-hidden="true" />
        ) : (
          <span className="rcp-strip__glyph" aria-hidden="true">{PHASE_GLYPH[phase]}</span>
        )}
        {PHASE_WORD[phase]}
        {phase === 'queued' && queuePosition != null && (
          <span className="rcp-strip__sub"> - position {queuePosition} of {queueLength}</span>
        )}
      </span>

      {phase !== 'idle' && (
        <>
          <span className="rcp-meter" aria-hidden="true">
            {grid.map(({ task, rows }) => (
              <span className="rcp-meter__group" key={task.id}>
                {rows.map((l) => (
                  <i
                    key={l.run}
                    className={`rcp-meter__cell rcp-meter__cell--${l.state}${
                      l.state === 'done' && (l.timed_out || (l.exit_code ?? 0) !== 0)
                        ? ' rcp-meter__cell--bad' : ''}`}
                  />
                ))}
              </span>
            ))}
          </span>
          <span className="rcp-strip__stat">{done}/{expected} lanes</span>
          <span className="rcp-strip__stat rcp-num">
            <span className="rcp-dim">timer </span>{fmtClock(elapsed)}
          </span>
          {/* The open panel shows the exact totals and speed below the lanes, so the strip carries them only while collapsed. */}
          {!open && (
            <span className="rcp-strip__stat rcp-num rcp-strip__tokens">
              {undercount && <span aria-hidden="true">≈</span>}
              {fmtTokens(totalIn)}&#8593; {fmtTokens(totalOut)}&#8595;
            </span>
          )}
          {!open && speed && <span className="rcp-strip__stat rcp-num rcp-dim">{speed}</span>}
        </>
      )}
    </>
  );

  return (
    <section className={`rcp rcp--${phase}${open ? '' : ' rcp--collapsed'}`}>
      <div className="rcp-strip">
        <button
          type="button"
          className="rcp-strip__toggle"
          aria-expanded={open}
          aria-controls="rcp-body"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="rcp-strip__chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
          <span className="rcp-sr">{open ? 'Collapse run panel' : 'Expand run panel'}</span>
          {strip}
        </button>
        {action && <div className="rcp-strip__action">{action}</div>}
      </div>

      <div id="rcp-body" className="rcp-body" hidden={!open}>
        {phase === 'idle' ? (
          <p className="rcp-empty">
            No runs yet.
            {mode === 'practice' && " Practice runs also stream each lane's terminal live."}
          </p>
        ) : (
          <>
            <div className="rcp-grid" role="grid" aria-label="Lanes">
              {grid.map(({ task, rows }, i) => (
                <div className="rcp-grid__col" key={task.id} role="row">
                  <div className="rcp-grid__head" title={task.label}>
                    <span className="rcp-grid__num">{i + 1}</span>
                    {taskShort(task.id)}
                  </div>
                  {rows.map((lane) => (
                    <LaneCell
                      key={lane.run}
                      lane={lane}
                      selected={selected?.task === lane.task && selected?.run === lane.run}
                      onSelect={() => onSelectLane(lane.task, lane.run)}
                    />
                  ))}
                </div>
              ))}
            </div>

            <div className="rcp-runnav">
              <button
                type="button"
                className="rcp-runnav__arrow"
                onClick={() => onSelectLane(selected?.task ?? firstTask, (selected?.run ?? 1) - 1)}
                disabled={(selected?.run ?? 1) <= 1}
                aria-label="Previous run"
              >
                &#8592;
              </button>
              <span className="rcp-runnav__label rcp-num">
                Run {selected?.run ?? 1} / {Math.max(1, progress?.n_runs || nRuns || 1)}
              </span>
              <button
                type="button"
                className="rcp-runnav__arrow"
                onClick={() => onSelectLane(selected?.task ?? firstTask, (selected?.run ?? 1) + 1)}
                disabled={(selected?.run ?? 1) >= Math.max(1, progress?.n_runs || nRuns || 1)}
                aria-label="Next run"
              >
                &#8594;
              </button>
            </div>

            <div className="rcp-totals">
              <span className="rcp-totals__headline rcp-num">
                input {undercount && <span aria-hidden="true">≈</span>}
                {fmtTokens(totalIn, true)}&#8593;
              </span>
              <span className="rcp-totals__headline rcp-num">
                output {fmtTokens(totalOut, true)}&#8595;
              </span>
              {speed && <span className="rcp-totals__headline rcp-num">{speed}</span>}
              <span className="rcp-totals__rest rcp-num">
                {total?.requests ?? 0} model call{(total?.requests ?? 0) === 1 ? '' : 's'}
              </span>
              {undercount && (
                <span
                  className="rcp-chip"
                  title={`${total?.tokenize_errors} request${
                    total?.tokenize_errors === 1 ? '' : 's'} could not be tokenized, so the new-token count is lower than true usage. It does not affect whether the run succeeds.`}
                >
                  ⓘ tokens ≈ undercount
                </span>
              )}
            </div>
          </>
        )}
      </div>

      <p className="rcp-sr" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </p>
    </section>
  );
};

export default RunControlPanel;
